<script setup lang="ts">
import { ArcElement, Chart as ChartJS, Tooltip, type ChartData, type ChartOptions } from 'chart.js'
import { computed } from 'vue'
import { Pie } from 'vue-chartjs'

import { MISOProgram } from '@/composables/eos/program-earnings-dashboard'
import type { ProgramEarningsYearly } from '@/types/eos/ProgramEarningsDashboard'

const emit = defineEmits<{ select: [{ program: MISOProgram }] }>()

/**
 * Pie chart showing total USD earnings broken down by energy program for a
 * given year. Each slice is the sum of all monthly entries for that program.
 *
 * @prop data - Flat list of per-program monthly earnings entries.
 */
const props = defineProps<{ data?: ProgramEarningsYearly }>()

// Legend is rendered in HTML so we only need ArcElement + Tooltip from ChartJS.
ChartJS.register(ArcElement, Tooltip)

const PROGRAMS: MISOProgram[] = [MISOProgram.DAPR, MISOProgram.OR]

const LEGEND_LABELS: Record<MISOProgram, string> = {
  [MISOProgram.DAPR]: 'Day-Ahead Price Response',
  [MISOProgram.OR]: 'Operating Reserves',
  [MISOProgram.All]: '',
}

// Same palette as ProgramEarningsStackChart for visual consistency.
const PROGRAM_COLORS: Record<MISOProgram, string> = {
  [MISOProgram.DAPR]: '#0bcd92',
  [MISOProgram.OR]: '#9fccfa',
  [MISOProgram.All]: '',
}

const totalByProgram = computed<Record<MISOProgram, number>>(() => {
  const totals: Record<MISOProgram, number> = {
    [MISOProgram.DAPR]: 0,
    [MISOProgram.OR]: 0,
    [MISOProgram.All]: 0,
  }
  if (!props.data) return totals
  for (const entry of props.data) {
    if (PROGRAMS.includes(entry.program)) {
      totals[entry.program] += entry.total_earnings_usd
    }
  }
  return totals
})

const grandTotal = computed(() => PROGRAMS.reduce((sum, p) => sum + totalByProgram.value[p], 0))

const chartData = computed<ChartData<'pie', number[], string>>(() => ({
  labels: PROGRAMS,
  datasets: [
    {
      data: PROGRAMS.map((p) => totalByProgram.value[p]),
      backgroundColor: PROGRAMS.map((p) => PROGRAM_COLORS[p]),
      borderWidth: 0,
    },
  ],
}))

const chartOptions = computed<ChartOptions<'pie'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  onClick: (_event, elements) => {
    if (!elements.length) return
    emit('select', { program: PROGRAMS[elements[0].index] })
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const value = ctx.parsed as number
          const total = (ctx.dataset.data as number[]).reduce((a, b) => a + b, 0)
          const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0'
          const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
          return `${formatted} (${pct}%)`
        },
      },
    },
  },
}))
</script>

<template>
  <div class="w-full h-full">
    <div v-if="data?.length" class="flex h-full">
      <div class="h-9/10">
        <Pie :data="chartData" :options="chartOptions" />
      </div>
      <div class="flex flex-col justify-center gap-3 pl-6">
        <div v-for="program in PROGRAMS" :key="program" class="flex items-center gap-2">
          <span class="w-3 h-3 rounded-full shrink-0" :style="{ backgroundColor: PROGRAM_COLORS[program] }" />
          <span class="text-sm">{{ LEGEND_LABELS[program] }}</span>
          <span class="text-sm text-gray-400 ml-auto">
            {{ grandTotal > 0 ? ((totalByProgram[program] / grandTotal) * 100).toFixed(1) + '%' : '—' }}
          </span>
        </div>
      </div>
    </div>
    <div v-else class="h-full flex items-center justify-center">
      <span class="text-gray-500">Please select a site and year.</span>
    </div>
  </div>
</template>
